import { Chat } from '@mui/icons-material'
import { AppBar, Button, Stack, Toolbar, Typography, styled } from '@mui/material'
import { FC, useContext } from 'react'
import { Link } from 'react-router-dom'

import { AppContext } from '../../../AppContext'
import { Utils } from '../../../Utils'
import useOrgInfo from '../../../hooks/useOrgInfo'
import useSelector from '../../../hooks/useSelector'
import { selectPageSubtitle, selectPageTitle } from '../../../store/page'
import useBrand from '../../../tenant/useBrand'
import Image from '../Image'

import NavbarAvatar from './NavbarAvatar'

interface NavbarProps {
  authenticated: boolean
}

const Navbar: FC<NavbarProps> = ({ authenticated }) => {
  const { orgID } = useContext(AppContext)
  const { orgInfo } = useOrgInfo()
  const brand = useBrand()
  const title = useSelector(selectPageTitle)
  const subtitle = useSelector(selectPageSubtitle)

  return (
    <StyledAppBar
      className="no-print"
      elevation={0}
      position="static"
      sx={{ backgroundImage: `url(${Utils.requireFromCDN('header-bg.png')})` }}
    >
      <Toolbar sx={{ gap: 2 }}>
        <Link to="/app" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          {brand.vanity ? (
            // Vanity orgs show the ARISTOTLE.ai wordmark art (matches the login
            // hero); falls back to the headline text if the asset is missing.
            <Image
              alt={brand.headline}
              src={Utils.requireFromCDN('aristotle-wordmark.png')}
              height={26}
              fallback={
                <Typography component="span" sx={{ color: 'white', fontWeight: 'bold' }} variant="h6">
                  {brand.headline}
                </Typography>
              }
            />
          ) : (
            <img
              src={Utils.requireFromCDN('itg-logo-light.png')}
              height="45"
              style={{ height: 45, maxHeight: 45 }}
            />
          )}
        </Link>

        <PageTitleContainer>
          <Typography
            align="center"
            color="inherit"
            component="div"
            sx={{ lineHeight: 'normal' }}
            variant="h6"
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography align="center" color="inherit">
              {subtitle}
            </Typography>
          )}
        </PageTitleContainer>

        <Stack alignItems="center" direction="row" spacing={3} sx={{ ml: 'auto' }}>
          {authenticated && (
            <Button
              component={Link}
              to="/app/chat"
              size="small"
              startIcon={<Chat />}
              variant="contained"
              sx={{ whiteSpace: 'nowrap' }}
            >
              Start chat
            </Button>
          )}

          <Stack alignItems="center">
            {orgInfo.logos.light ? (
              <Image
                alt={orgInfo.name}
                fallback={
                  <Typography component="span" sx={{ color: 'white' }} variant="h6">
                    {orgInfo.name}
                  </Typography>
                }
                height={45}
                skeleton
                src={Utils.requireOrgAssetFromCDN(orgID, `org_logos/${orgInfo.logos.light}`)}
              />
            ) : (
              <Typography component="span" color="inherit" variant="h6">
                {orgInfo.name}
              </Typography>
            )}
          </Stack>

          {authenticated && <NavbarAvatar />}
        </Stack>
      </Toolbar>
    </StyledAppBar>
  )
}

const PageTitleContainer = styled(Stack)`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`

const StyledAppBar = styled(AppBar)`
  background-size: 120% 120%;
  background-position-x: 71%;
  background-repeat: no-repeat;
`

export default Navbar
